import React, { useState, useMemo } from 'react';
import Icon from 'components/AppIcon';
import Tooltip from 'components/Tooltip';
import BatchStatusBadge from '../../active-batch-management/components/BatchStatusBadge';
import EmptyState from 'components/EmptyState';

const SORT_FIELDS = ['display_name', 'weight_t', 'yield_est', 'exposure', 'latentPnl', 'age', 'batch_status'];

function calcAge(createdAt) {
  if (!createdAt) return 0;
  return Math.floor((Date.now() - new Date(createdAt)?.getTime()) / (1000 * 60 * 60 * 24));
}

function calcExposure(batch) {
  return (parseFloat(batch?.purchase_price) || 0) * (parseFloat(batch?.weight_t) || 0);
}

function getEffectiveYield(batch) {
  const yieldActual = batch?.yield_actual != null ? parseFloat(batch?.yield_actual) : null;
  const yieldEst = parseFloat(batch?.yield_est) || 85;
  return {
    value: yieldActual !== null ? yieldActual : yieldEst,
    isReal: yieldActual !== null,
  };
}

function calcRecoverableValue(batch, benchmarkPrice) {
  const { value: yield_pct } = getEffectiveYield(batch);
  return benchmarkPrice * (yield_pct / 100);
}

function calcLatentPnl(batch, benchmarkPrice) {
  const recoverablePerTon = calcRecoverableValue(batch, benchmarkPrice);
  const entryPrice = parseFloat(batch?.purchase_price) || 0;
  const weight = parseFloat(batch?.weight_t) || 0;
  return (recoverablePerTon - entryPrice) * weight;
}

const COLUMN_TOOLTIPS = {
  'Categoría': { title: 'Categoría / Lote', description: 'Nombre de la categoría de chatarra y código único del lote.' },
  'Peso (t)': { title: 'Peso en Toneladas', description: 'Peso neto del lote en toneladas métricas. Editable directamente en la tabla.', example: '2.50 t' },
  'Yield (%)': { title: 'Rendimiento (%)', description: 'Porcentaje de metal recuperable. Si el lote tiene un Yield Real registrado (badge "Real"), se usa ese valor para los cálculos. Si no, se usa el Yield Estimado.', example: '85% = 850 kg de metal puro por tonelada' },
  'Val. Recup. (€/t)': { title: 'Valor Recuperable por Tonelada (€/t)', description: 'Precio de mercado (benchmark) × Yield efectivo (Real si disponible, Estimado si no). Representa el ingreso esperado por tonelada procesada.', example: 'Benchmark 10.000 €/t × 85% = 8.500 €/t' },
  'Exposición (€)': { title: 'Exposición Total (€)', description: 'Capital comprometido en este lote. Calculado como Precio de Compra × Peso (t).', example: '10.000 €/t × 2.5 t = 25.000 €' },
  'Benchmark (€/t)': { title: 'Precio de Mercado Actual (€/t)', description: 'Último precio de referencia LME/mercado para este metal. Se actualiza en tiempo real desde la sección Datos de Mercado.' },
  'Precio Entrada (€/t)': { title: 'Precio de Compra (€/t)', description: 'Precio al que se adquirió el lote. Sirve como base para calcular el P&L Latente.' },
  'P&L Latente (€)': { title: 'Ganancia/Pérdida Latente (€)', description: 'Diferencia entre el Valor Recuperable actual y el coste de entrada, multiplicado por el peso. Verde = ganancia potencial, Rojo = pérdida potencial.', example: '(8.500 - 8.000) €/t × 2.5 t = +1.250 €' },
  'Edad (días)': { title: 'Antigüedad del Lote (días)', description: 'Días transcurridos desde la creación del lote. Naranja = supera el umbral de alerta configurado en Ajustes.' },
  'Estado': { title: 'Estado del Lote', description: 'Fase actual del lote: En Espera → Procesando → Listo para Venta → Vendido.' },
  'Acciones': { title: 'Acciones Disponibles', description: 'Editar peso/yield, ver historial de cambios, marcar como vendido o eliminar el lote.' },
};

export default function StockTable({ batches, benchmarks, onMarkSold, onEditBatch, onDeleteBatch, onViewTimeline, statusFilter, onStatusFilterChange, onNavigateBatches }) {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('age');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedMetals, setExpandedMetals] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const benchmarkMap = useMemo(() => {
    const map = {};
    benchmarks?.forEach(b => { map[b.symbol] = parseFloat(b?.value) || 0; });
    return map;
  }, [benchmarks]);

  const getBenchmarkForBatch = (batch) => {
    const METAL_NAME_TO_SYMBOL = {
      'cobre': 'LME_CU',
      'aluminio': 'LME_AL',
      'zinc': 'LME_ZN',
      'plomo': 'LME_PB',
      'níquel': 'LME_NI',
      'niquel': 'LME_NI',
      'estaño': 'LME_SN',
      'estano': 'LME_SN',
      'acero inox': 'REG_INOX',
      'inox': 'REG_INOX',
      'hierro': 'REG_FE',
      'acero': 'REG_FE',
      'latón': 'REG_LAT',
      'laton': 'REG_LAT',
      'bronce': 'MAN_BRZ',
    };
    const metalKey = (batch?.metal_name || '')?.toLowerCase()?.trim();
    // 1. Exact match via map
    const mappedSymbol = METAL_NAME_TO_SYMBOL?.[metalKey];
    if (mappedSymbol && benchmarkMap?.[mappedSymbol] !== undefined) {
      return benchmarkMap?.[mappedSymbol];
    }
    // 2. Match on batch.benchmark_symbol if present
    if (batch?.benchmark_symbol && benchmarkMap?.[batch?.benchmark_symbol] !== undefined) {
      return benchmarkMap?.[batch?.benchmark_symbol];
    }
    // 3. No match found
    return 0;
  };

  const filtered = useMemo(() => {
    let list = batches || [];
    if (statusFilter && statusFilter !== 'all') {
      list = list?.filter(b => b?.batch_status === statusFilter);
    }
    if (search?.trim()) {
      const q = search?.toLowerCase();
      list = list?.filter(b =>
        b?.metal_name?.toLowerCase()?.includes(q) ||
        b?.category_name?.toLowerCase()?.includes(q) ||
        b?.batch_code?.toLowerCase()?.includes(q)
      );
    }
    return list;
  }, [batches, statusFilter, search]);

  const grouped = useMemo(() => {
    const groups = {};
    filtered?.forEach(b => {
      const metal = b?.metal_name || 'Sin Metal';
      if (!groups?.[metal]) groups[metal] = [];
      groups?.[metal]?.push(b);
    });
    return groups;
  }, [filtered]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const sortedRows = (rows) => {
    return [...rows]?.sort((a, b) => {
      let aVal, bVal;
      const bpA = getBenchmarkForBatch(a);
      const bpB = getBenchmarkForBatch(b);
      if (sortField === 'display_name') { aVal = a?.category_name || ''; bVal = b?.category_name || ''; }
      else if (sortField === 'weight_t') { aVal = parseFloat(a?.weight_t) || 0; bVal = parseFloat(b?.weight_t) || 0; }
      else if (sortField === 'yield_est') { aVal = parseFloat(a?.yield_est) || 0; bVal = parseFloat(b?.yield_est) || 0; }
      else if (sortField === 'exposure') { aVal = calcExposure(a); bVal = calcExposure(b); }
      else if (sortField === 'latentPnl') { aVal = calcLatentPnl(a, bpA); bVal = calcLatentPnl(b, bpB); }
      else if (sortField === 'age') { aVal = calcAge(a?.created_at); bVal = calcAge(b?.created_at); }
      else if (sortField === 'batch_status') { aVal = a?.batch_status || ''; bVal = b?.batch_status || ''; }
      else { aVal = 0; bVal = 0; }
      if (typeof aVal === 'string') return sortDir === 'asc' ? aVal?.localeCompare(bVal) : bVal?.localeCompare(aVal);
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  const toggleMetal = (metal) => setExpandedMetals(prev => ({ ...prev, [metal]: !prev?.[metal] }));

  const startEdit = (batch) => {
    setEditingId(batch?.id);
    setEditValues({ weight_t: batch?.weight_t, yield_est: batch?.yield_est });
  };

  const saveEdit = (batch) => {
    onEditBatch?.(batch?.id, { weight_t: parseFloat(editValues?.weight_t), yield_est: parseFloat(editValues?.yield_est) });
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    setDeleting(true);
    await onDeleteBatch?.(id);
    setDeleting(false);
    setConfirmDeleteId(null);
  };

  const SortIcon = ({ field }) => (
    <span className="ml-1 inline-flex flex-col" style={{ lineHeight: 0 }}>
      <Icon name={sortField === field && sortDir === 'asc' ? 'ChevronUp' : 'ChevronsUpDown'} size={12} color={sortField === field ? 'var(--color-accent)' : 'var(--color-muted-foreground)'} />
    </span>
  );

  const STATUS_OPTIONS = [
    { value: 'all', label: 'Todos' },
    { value: 'En Espera', label: 'En Espera' },
    { value: 'Procesando', label: 'Procesando' },
    { value: 'Listo para Venta', label: 'Listo para Venta' },
    { value: 'Completado', label: 'Vendido' },
  ];

  const getEmptyState = () => {
    const hasSearch = search?.trim();
    const hasStatusFilter = statusFilter && statusFilter !== 'all';

    if (hasSearch && hasStatusFilter) {
      return (
        <EmptyState
          icon="Filter"
          title="Sin resultados para este filtro"
          description="Ninguna categoría o metal coincide con los filtros aplicados."
          ctaLabel="Limpiar filtros"
          ctaAction={() => { setSearch(''); onStatusFilterChange('all'); }}
          secondaryCtaLabel="Ver todo el inventario"
          secondaryCtaAction={() => { setSearch(''); onStatusFilterChange('all'); }}
          colSpan={12}
        />
      );
    }
    if (hasSearch) {
      return (
        <EmptyState
          icon="Search"
          title="Sin resultados"
          description="Ningún metal o categoría coincide con tu búsqueda."
          ctaLabel="Limpiar búsqueda"
          ctaAction={() => setSearch('')}
          secondaryCtaLabel="Ver todo el inventario"
          secondaryCtaAction={() => { setSearch(''); onStatusFilterChange('all'); }}
          colSpan={12}
        />
      );
    }
    if (hasStatusFilter) {
      return (
        <EmptyState
          icon="Filter"
          title="Sin resultados para este filtro"
          description="Ninguna categoría o metal coincide con los filtros aplicados."
          ctaLabel="Limpiar filtros"
          ctaAction={() => onStatusFilterChange('all')}
          secondaryCtaLabel="Ver todo el inventario"
          secondaryCtaAction={() => onStatusFilterChange('all')}
          colSpan={12}
        />
      );
    }
    // No inventory at all
    return (
      <EmptyState
        icon="Warehouse"
        title="Inventario vacío"
        description="No hay lotes en inventario. Los lotes activos y en proceso aparecerán aquí."
        ctaLabel="Ir a Lotes"
        ctaAction={onNavigateBatches}
        colSpan={12}
      />
    );
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" style={{ boxShadow: 'var(--shadow-sm)' }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border-b border-border">
        <div className="flex items-center gap-2 flex-1">
          <Icon name="Table" size={18} color="var(--color-accent)" strokeWidth={2} />
          <h2 className="font-semibold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}>Stock por Metal / Categoría</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Icon name="Search" size={14} color="var(--color-muted-foreground)" className="absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Buscar metal o categoría..."
              value={search}
              onChange={e => setSearch(e?.target?.value)}
              className="pl-8 pr-3 py-1.5 text-sm bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-accent"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif', width: '200px' }}
            />
          </div>
          {/* Status filter */}
          <Tooltip title="Filtrar por Estado" description="Muestra solo los lotes con el estado seleccionado. 'Todos' incluye todos los estados.">
            <select
              value={statusFilter}
              onChange={e => onStatusFilterChange(e?.target?.value)}
              className="px-3 py-1.5 text-sm bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-accent"
              style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
            >
              {STATUS_OPTIONS?.map(o => <option key={o?.value} value={o?.value}>{o?.label}</option>)}
            </select>
          </Tooltip>
        </div>
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-8"></th>
              {[
                { field: 'display_name', label: 'Categoría' },
                { field: 'weight_t', label: 'Peso (t)' },
                { field: 'yield_est', label: 'Yield (%)' },
                { field: null, label: 'Val. Recup. (€/t)' },
                { field: 'exposure', label: 'Exposición (€)' },
                { field: null, label: 'Benchmark (€/t)' },
                { field: null, label: 'Precio Entrada (€/t)' },
                { field: 'latentPnl', label: 'P&L Latente (€)' },
                { field: 'age', label: 'Edad (días)' },
                { field: 'batch_status', label: 'Estado' },
                { field: null, label: 'Acciones' },
              ]?.map(col => (
                <th
                  key={col?.label}
                  className={`text-left px-3 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap ${col?.field ? 'cursor-pointer hover:text-foreground select-none' : ''}`}
                  onClick={() => col?.field && handleSort(col?.field)}
                >
                  {COLUMN_TOOLTIPS?.[col?.label] ? (
                    <Tooltip
                      title={COLUMN_TOOLTIPS?.[col?.label]?.title}
                      description={COLUMN_TOOLTIPS?.[col?.label]?.description}
                      example={COLUMN_TOOLTIPS?.[col?.label]?.example}
                    >
                      {col?.label}{col?.field && <SortIcon field={col?.field} />}
                    </Tooltip>
                  ) : (
                    <>{col?.label}{col?.field && <SortIcon field={col?.field} />}</>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(grouped)?.length === 0 ? (
              getEmptyState()
            ) : (
              Object.entries(grouped)?.map(([metal, rows]) => {
                const isExpanded = expandedMetals?.[metal] !== false; // default expanded
                const metalExposure = rows?.reduce((s, b) => s + calcExposure(b), 0);
                const metalWeight = rows?.reduce((s, b) => s + (parseFloat(b?.weight_t) || 0), 0);
                const bp = getBenchmarkForBatch(rows?.[0]);
                const metalPnl = rows?.reduce((s, b) => s + calcLatentPnl(b, getBenchmarkForBatch(b)), 0);

                return (
                  <React.Fragment key={metal}>
                    {/* Metal group header row */}
                    <tr
                      className="bg-muted/30 border-b border-border cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => toggleMetal(metal)}
                    >
                      <td className="px-4 py-2.5">
                        <Icon name={isExpanded ? 'ChevronDown' : 'ChevronRight'} size={14} color="var(--color-muted-foreground)" />
                      </td>
                      <td className="px-3 py-2.5 font-semibold text-foreground" colSpan={2}>
                        <span className="flex items-center gap-2">
                          <Icon name="Layers" size={14} color="var(--color-accent)" />
                          {metal}
                          <span className="text-xs text-muted-foreground font-normal">({rows?.length} lotes)</span>
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground text-xs">{metalWeight?.toFixed(2)} t</td>
                      <td className="px-3 py-2.5" colSpan={3}></td>
                      <td className="px-3 py-2.5 font-mono text-xs">
                        <span className={metalExposure > 0 ? 'text-foreground' : 'text-muted-foreground'}>
                          {metalExposure?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €
                        </span>
                      </td>
                      <td className="px-3 py-2.5" colSpan={2}></td>
                      <td className="px-3 py-2.5 font-mono text-xs">
                        <span className={metalPnl >= 0 ? 'text-emerald-500' : 'text-red-500'}>
                          {metalPnl >= 0 ? '+' : ''}{metalPnl?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €
                        </span>
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                    {/* Category rows */}
                    {isExpanded && sortedRows(rows)?.map(batch => {
                      const benchmarkPrice = getBenchmarkForBatch(batch);
                      const exposure = calcExposure(batch);
                      const recoverableVal = calcRecoverableValue(batch, benchmarkPrice);
                      const latentPnl = calcLatentPnl(batch, benchmarkPrice);
                      const age = calcAge(batch?.created_at);
                      const isEditing = editingId === batch?.id;

                      return (
                        <tr key={batch?.id} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-2.5"></td>
                          <td className="px-3 py-2.5">
                            <div>
                              <p className="font-medium text-foreground text-sm">{batch?.category_name || '—'}</p>
                              <p className="text-xs text-muted-foreground">{batch?.batch_code || batch?.id?.slice(0, 8)}</p>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 font-mono text-sm">
                            {isEditing ? (
                              <input
                                type="number"
                                value={editValues?.weight_t}
                                onChange={e => setEditValues(v => ({ ...v, weight_t: e?.target?.value }))}
                                className="w-20 px-2 py-1 text-xs bg-muted border border-accent rounded focus:outline-none"
                              />
                            ) : (
                              <span>{(parseFloat(batch?.weight_t) || 0)?.toFixed(2)}</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 font-mono text-sm">
                            {isEditing ? (
                              <input
                                type="number"
                                value={editValues?.yield_est}
                                onChange={e => setEditValues(v => ({ ...v, yield_est: e?.target?.value }))}
                                className="w-16 px-2 py-1 text-xs bg-muted border border-accent rounded focus:outline-none"
                              />
                            ) : (
                              <span className="flex items-center gap-1.5">
                                {(() => {
                                  const { value, isReal } = getEffectiveYield(batch);
                                  return (
                                    <>
                                      <span className={isReal ? 'text-emerald-500 font-semibold' : 'text-foreground'}>
                                        {value?.toFixed(1)}%
                                      </span>
                                      {isReal && (
                                        <span className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-semibold bg-emerald-500/15 text-emerald-500 leading-none">
                                          Real
                                        </span>
                                      )}
                                    </>
                                  );
                                })()}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 font-mono text-sm text-foreground">
                            {benchmarkPrice > 0 ? recoverableVal?.toLocaleString('es-ES', { maximumFractionDigits: 0 }) : '—'}
                          </td>
                          <td className="px-3 py-2.5 font-mono text-sm">
                            {exposure?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €
                          </td>
                          <td className="px-3 py-2.5 font-mono text-sm text-muted-foreground">
                            {benchmarkPrice > 0 ? benchmarkPrice?.toLocaleString('es-ES', { maximumFractionDigits: 0 }) : '—'}
                          </td>
                          <td className="px-3 py-2.5 font-mono text-sm">
                            {(parseFloat(batch?.purchase_price) || 0)?.toLocaleString('es-ES', { maximumFractionDigits: 0 })}
                          </td>
                          <td className="px-3 py-2.5 font-mono text-sm font-semibold">
                            <span className={latentPnl >= 0 ? 'text-emerald-500' : 'text-red-500'}>
                              {latentPnl >= 0 ? '+' : ''}{latentPnl?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-sm">
                            <span className={age > 30 ? 'text-amber-500 font-semibold' : age > 60 ? 'text-red-500 font-semibold' : 'text-foreground'}>
                              {age}
                            </span>
                          </td>
                          <td className="px-3 py-2.5">
                            <BatchStatusBadge status={batch?.batch_status} />
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-1">
                              {isEditing ? (
                                <>
                                  <Tooltip title="Guardar cambios" description="Guarda el nuevo peso y yield del lote.">
                                    <button onClick={() => saveEdit(batch)} className="p-1.5 rounded hover:bg-emerald-100 text-emerald-600 transition-colors" title="Guardar">
                                      <Icon name="Check" size={13} color="currentColor" />
                                    </button>
                                  </Tooltip>
                                  <Tooltip title="Cancelar edición" description="Descarta los cambios y vuelve a la vista normal.">
                                    <button onClick={() => setEditingId(null)} className="p-1.5 rounded hover:bg-red-100 text-red-500 transition-colors" title="Cancelar">
                                      <Icon name="X" size={13} color="currentColor" />
                                    </button>
                                  </Tooltip>
                                </>
                              ) : (
                                <>
                                  <Tooltip title="Editar lote" description="Modifica el peso (t) y el yield estimado (%) de este lote.">
                                    <button onClick={() => startEdit(batch)} className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="Editar">
                                      <Icon name="Pencil" size={13} color="currentColor" />
                                    </button>
                                  </Tooltip>
                                  {onViewTimeline && (
                                    <Tooltip title="Ver historial" description="Muestra la línea de tiempo con todos los cambios de estado de este lote.">
                                      <button
                                        onClick={() => onViewTimeline(batch)}
                                        className="p-1.5 rounded hover:bg-violet-100 text-muted-foreground hover:text-violet-600 transition-colors"
                                        title="Ver historial"
                                      >
                                        <Icon name="History" size={13} color="currentColor" />
                                      </button>
                                    </Tooltip>
                                  )}
                                  {(batch?.batch_status === 'Listo para Venta' || batch?.batch_status === 'En Espera' || batch?.batch_status === 'Procesando') && (
                                    <Tooltip title="Registrar venta" description="Abre el formulario para registrar la venta de este lote y moverlo a Vendido.">
                                      <button onClick={() => onMarkSold?.(batch)} className="p-1.5 rounded hover:bg-emerald-100 text-emerald-600 transition-colors" title="Marcar como vendido">
                                        <Icon name="ShoppingCart" size={13} color="currentColor" />
                                      </button>
                                    </Tooltip>
                                  )}
                                  {confirmDeleteId === batch?.id ? (
                                    <>
                                      <Tooltip title="Confirmar eliminación" description="Esta acción es irreversible. El lote será eliminado permanentemente.">
                                        <button
                                          onClick={() => handleDelete(batch?.id)}
                                          disabled={deleting}
                                          className="p-1.5 rounded bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
                                          title="Confirmar eliminación"
                                        >
                                          {deleting ? <Icon name="Loader" size={13} color="currentColor" /> : <Icon name="Check" size={13} color="currentColor" />}
                                        </button>
                                      </Tooltip>
                                      <Tooltip title="Cancelar eliminación" description="Cancela la eliminación y mantiene el lote.">
                                        <button
                                          onClick={() => setConfirmDeleteId(null)}
                                          className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                          title="Cancelar"
                                        >
                                          <Icon name="X" size={13} color="currentColor" />
                                        </button>
                                      </Tooltip>
                                    </>
                                  ) : (
                                    <Tooltip title="Eliminar lote" description="Elimina este lote del inventario de forma permanente. Se pedirá confirmación.">
                                      <button
                                        onClick={() => setConfirmDeleteId(batch?.id)}
                                        className="p-1.5 rounded hover:bg-red-100 text-muted-foreground hover:text-red-500 transition-colors"
                                        title="Eliminar lote"
                                      >
                                        <Icon name="Trash2" size={13} color="currentColor" />
                                      </button>
                                    </Tooltip>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
